/**
 * Current raid roster — shared shapes and the tier/variant rules.
 *
 * The roster itself comes from GET /api/events/raids (LeekDuck via ScrapedDuck).
 * This module holds the parts worth pinning down away from the component: which
 * tiers render and in what order, how a tier reads, and — the one with real
 * consequences — which DEX VARIANT a given boss should be cross-referenced
 * against.
 *
 * Pure + storage-agnostic so it is unit-testable without a DOM or the network.
 */
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";
import { FORM_GROUPS_BY_SPECIES_ID } from "./generated/pokemonForms.generated";

/** The tiers the Profile panel renders, in the order it renders them. */
export const RAID_TIER_ORDER = ["1", "3", "5", "mega"] as const;
export type RaidTierKey = (typeof RAID_TIER_ORDER)[number];

/**
 * The same tiers, BIGGEST FIRST.
 *
 * RAID_TIER_ORDER runs 1 to Mega because the Profile panel reads as a ladder.
 * Anywhere the question is "what am I doing with my passes" the answer starts
 * at the top: a Mega or a 5-star is the raid worth planning for, a 1-star is
 * the one you do because you walked past it.
 *
 * Shared rather than declared per screen, because it was declared per screen
 * once already and the Meta page's two panels immediately disagreed with each
 * other about it.
 */
export const RAID_TIERS_BIGGEST_FIRST: readonly RaidTierKey[] = ["mega", "5", "3", "1"];

/** One boss as served by /api/events/raids. */
export interface RaidBossDto {
  name: string;
  speciesId?: number;
  /** Regional-form suffix (alola / galar / hisui / paldea), when applicable. */
  formSuffix?: string;
  tier: string;
  tierLabel: string;
  canBeShiny: boolean;
  isShadow: boolean;
  isMega: boolean;
  image?: string;
  types?: string[];
  /**
   * LeekDuck's 100% catch CPs (level 20, and level 25 weather boosted),
   * when the feed carried both. The Worker has sent this since the raid
   * hub; the client only started reading it for the Profile's Hundo Raid
   * CPs block (2026-09-04).
   */
  hundoCp?: { normal: number; boosted: number };
}

export function raidTierLabel(tier: RaidTierKey): string {
  switch (tier) {
    case "1": return "1★ raids";
    case "3": return "3★ raids";
    case "5": return "5★ raids";
    case "mega": return "Mega raids";
  }
}

/**
 * The dex variant a raid boss should be checked against.
 *
 * This matters because "do I have it?" has a different answer per raid type: a
 * Mega Gengar raid fills the MEGA dex (the trainer may well own a plain Gengar
 * and still need this), and a Shadow raid fills the SHADOW dex. Checking every
 * boss against the base dex would mark a Mega raid as "already caught" for
 * anyone who owns the ordinary species — the exact false negative that makes a
 * "what should I raid" panel worthless.
 *
 * Shadow is tested before Mega because LeekDuck has no Shadow-Mega raids: if a
 * label ever carried both, the shadow copy is the rarer dex and the better lead.
 */
export function raidBossVariant(boss: Pick<RaidBossDto, "isMega" | "isShadow">): PokedexVariant {
  if (boss.isShadow) return "shadow";
  if (boss.isMega) return "mega";
  return "pokemon";
}

/**
 * The app's own form id for a regional-variant boss, e.g. Galarian Darumaka
 * (#554, suffix "galar") → "darumaka-galar".
 *
 * Upstream tells us the region via the sprite filename; this resolves it against
 * the generated form registry so the row renders that form's artwork instead of
 * the base species. Returns null when the boss is not a regional variant, or
 * when the registry has no matching form — in which case the caller falls back
 * to the base species, which is what the app did before.
 */
export function raidBossFormId(boss: Pick<RaidBossDto, "speciesId" | "formSuffix">): string | null {
  if (!boss.formSuffix || typeof boss.speciesId !== "number") return null;
  const group = FORM_GROUPS_BY_SPECIES_ID.get(boss.speciesId);
  if (!group) return null;
  const suffix = `-${boss.formSuffix}`;
  const match = group.forms.find((f) => f.formId.endsWith(suffix));
  return match?.formId ?? null;
}
